import { useState, useEffect } from "react";
import { Link, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { FaSearch, FaUser } from '../utility/react-icons';
import { IoIosClose } from "react-icons/io";
import { useAuth } from "../context/AuthContext";
import KarkaThamizha from '../assets/KarkaThamizha.svg';
import image from "../assets/image.png";
import safeStorage from '../utility/safeStorage';
import { User, Settings, Edit, BookOpen, Star, Heart, Lock, LogOut } from 'lucide-react';
import { createSlug } from '../utility/HelperFunction'

/* ── Nav dropdown items data ────────────────────────────────────── */
const NAV_ITEMS = [
    { label: 'முகப்பு', to: '/' },
    {
        label: 'மதிப்புரை',
        children: [
            { label: 'எழுத்தாளர்கள்', to: '/authorreview', en: 'Author Reviews' },
            { label: 'வாசகர்கள்', to: '/userreview', en: 'Reader Reviews' },
        ],
    },
    {
        label: 'எழுத்தாளர்கள்',
        children: [
            { label: 'எழுத்தாளர் பட்டியல்', to: '/author', en: 'Author List' },
            { label: 'தொடர்கள்', to: '/authorseries', en: 'Series' },
        ],
    },
    {
        label: 'வெளியீடு',
        children: [
            { label: 'முன் வெளியீட்டுத் திட்டம்', to: '/bookprereleases', en: 'Pre-release Plan' },
            { label: 'சிறப்புத் தள்ளுபடி', to: '/bookoffers', en: 'Special Offers' },
        ],
    },
    {
        label: 'நிகழ்வுகள்',
        children: [
            { label: 'புத்தகக் கண்காட்சி', to: '/bookfairs', en: 'Book Fairs' },
            { label: 'நூல் வெளியீட்டு விழா', to: '/booklaunches', en: 'Book Launches' },
            { label: 'விருது வழங்கும் விழா', to: '/awards', en: 'Awards' },
        ],
    },
    {
        label: 'மற்றவை',
        children: [
            { label: 'தொடர்புக்கு', to: '/ContactUs', en: 'Contact Us' },
            // { label: 'உங்கள் கருத்து', to: '/Feedback', en: 'Feedback' },
        ],
    },
];

/* ── User menu items ────────────────────────────────────────────── */
const menuItems = (userId, userName) => [
    { Icon: Settings, label: 'விருப்ப பட்டியல்', en: 'Wishlist', to: `/user/${userId}/${createSlug(userName)}` },
    { Icon: Edit, label: 'என் சுயவிவரம்', en: 'Edit Profile', to: '/myprofile' },
    { Icon: BookOpen, label: 'என் விமர்சனங்கள்', en: 'My Reviews', to: `/user/${userId}/${createSlug(userName)}`, countKey: 'review' },
    // { Icon: Star, label: 'என் மதிப்பீடுகள்', en: 'My Ratings', to: `/user/${userId}/${createSlug(userName)}`, countKey: 'rating' },
    // { Icon: Heart, label: 'என் விருப்பப்பட்டியல்', en: 'My Wishlist', to: '/my-wishlist' },
    { Icon: Lock, label: 'கடவுச்சொல் மாற்று', en: 'Change Password', to: '/changePassword' },
];

/* ────────────────────────────────────────────────────────────────── */
export function MainNavbar() {
    const { isLoggedIn, userData, logout, reviewCount, ratingCount } = useAuth();
    const [showUserMenu, setShowUserMenu] = useState(false);
    const navigate = useNavigate();
    const location = useLocation();

    const initials = userData?.name?.charAt(0).toUpperCase() || 'U';
    const userId = userData?.userId;
    const userName = userData?.userName;

    const go = (path) => { setShowUserMenu(false); navigate(path); };

    useEffect(() => {
        const handleEsc = (e) => {
            if (e.key === 'Escape') setShowUserMenu(false);
        };
        if (showUserMenu) document.addEventListener('keydown', handleEsc);
        return () => document.removeEventListener('keydown', handleEsc);
    }, [showUserMenu]);

    const closeMenu = () => {
        const navCollapse = document.getElementById('mainNav');
        if (navCollapse && navCollapse.classList.contains('show')) {
            // Use Bootstrap's Collapse API if available, else toggle class manually
            if (window.bootstrap?.Collapse) {
                window.bootstrap.Collapse.getInstance(navCollapse)?.hide();
            } else {
                navCollapse.classList.remove('show');
            }
        }
    };

    return (
        <nav className="navbar navbar-expand-lg bg-light shadow-sm sticky-top"
            aria-label="முதன்மை வழிசெலுத்தல்">
            <div className="container-fluid">

                {/* Brand */}
                <Link to="/" className="navbar-brand ps-2">
                    <img src={KarkaThamizha} alt="கற்க தமிழா"
                        className="navbar-logo" width={160} height={34}
                        fetchPriority="high" decoding="async" />
                </Link>

                {/* Mobile toggler */}
                <button className="navbar-toggler" type="button"
                    data-bs-toggle="collapse" data-bs-target="#mainNav"
                    aria-controls="mainNav" aria-expanded="false"
                    aria-label="வழிசெலுத்தலை திற / மூடு">
                    <span className="navbar-toggler-icon" />
                </button>

                {/* Nav links */}
                <div className="collapse navbar-collapse" id="mainNav">
                    <ul className="navbar-nav ms-auto align-items-lg-center gap-lg-1">

                        {NAV_ITEMS.map(({ label, to, children }) =>
                            !children ? (
                                <li key={label} className="nav-item">
                                    <NavLink to={to} className="nav-link" onClick={closeMenu}>{label}</NavLink>
                                </li>
                            ) : (
                                <li key={label} className="nav-item dropdown">
                                    <NavLink to="#" className="nav-link dropdown-toggle"
                                        data-bs-toggle="dropdown" aria-expanded="false">
                                        {label}
                                    </NavLink>
                                    <ul className="dropdown-menu custom-menu">
                                        {children.map(({ label: cl, to: ct, en }) => (
                                            <li key={ct}>
                                                <NavLink to={ct} className="dropdown-item custom-item" onClick={closeMenu}
                                                    title={en}>
                                                    {cl}
                                                </NavLink>
                                            </li>
                                        ))}
                                    </ul>
                                </li>
                            )
                        )}

                        {/* Login / User avatar */}
                        <li className="nav-item ms-lg-2">
                            {!isLoggedIn ? (
                                <button className="btn nav-link" aria-label="Login" title="Login"
                                    onClick={() => navigate('/login', { state: { from: location.pathname } })}>
                                    <User size={18} aria-hidden="true" />
                                </button>
                            ) : (
                                <div className="position-relative">

                                    {/* Avatar */}
                                    <button className="usr-avatar" aria-label="பயனர் மெனு"
                                        aria-expanded={showUserMenu} aria-haspopup="true"
                                        title="User menu"
                                        onClick={() => setShowUserMenu(p => !p)}>
                                        {initials}
                                    </button>

                                    {showUserMenu && (
                                        <>
                                            {/* Backdrop */}
                                            <div className="usr-backdrop"
                                                onClick={() => setShowUserMenu(false)} />

                                            {/* Dropdown */}
                                            <div className="usr-menu" role="menu">

                                                {/* Header — navy: white 14.7:1 ✅ */}
                                                <div className="usr-menu__header">
                                                    <div className="d-flex align-items-center gap-3">
                                                        <div className="usr-menu__initials" aria-hidden="true">
                                                            {initials}
                                                        </div>
                                                        <p className="text-white fw-semibold mb-0"
                                                            style={{ fontSize: '0.875rem' }}>
                                                            {userName}
                                                            {userData?.name?.trim() &&
                                                                <span style={{ opacity: 0.75 }}> ({userData.name})</span>}
                                                        </p>
                                                    </div>
                                                </div>

                                                {/* Option B gradient underline */}
                                                <div className="usr-menu__line" aria-hidden="true" />

                                                {/* Items */}
                                                <div className="py-1" role="none">
                                                    {menuItems(userId, userName).map(
                                                        ({ Icon, label, en, to, countKey }) => (
                                                            <button key={en} className="usr-item"
                                                                onClick={() => go(to)} title={en}
                                                                role="menuitem">
                                                                <Icon size={14} aria-hidden="true"
                                                                    className="usr-item__icon" />
                                                                <span>{label}</span>
                                                                {countKey === 'review' && reviewCount > 0 &&
                                                                    <span className="usr-item__badge">{reviewCount}</span>}
                                                                {countKey === 'rating' && ratingCount > 0 &&
                                                                    <span className="usr-item__badge">{ratingCount}</span>}
                                                            </button>
                                                        )
                                                    )}

                                                    <hr className="dropdown-divider my-1" />

                                                    <button className="usr-item usr-item--danger"
                                                        onClick={() => { setShowUserMenu(false); logout(); }}
                                                        title="Logout" role="menuitem">
                                                        <LogOut size={14} aria-hidden="true"
                                                            className="usr-item__icon" />
                                                        <span>வெளியேறு</span>
                                                    </button>
                                                </div>

                                            </div>
                                        </>
                                    )}
                                </div>
                            )}
                        </li>

                    </ul>
                </div>
            </div>
        </nav>
    );
}
